import { useEffect, useState } from "react";
import theme from "../../data/theme";

const { colors, fonts, radius, fontSize, fontWeight } = theme;

const SPIN_KEYFRAMES_ID = "action-button-spin-keyframes";

// Injects the spinner @keyframes once — inline styles can't declare keyframes,
// and this keeps ActionButton dependency-free (no CSS module / styled-components).
function ensureSpinKeyframes() {
  if (typeof document === "undefined" || document.getElementById(SPIN_KEYFRAMES_ID)) return;
  const style = document.createElement("style");
  style.id = SPIN_KEYFRAMES_ID;
  style.textContent = "@keyframes actionButtonSpin { to { transform: rotate(360deg); } }";
  document.head.appendChild(style);
}

const TONES = {
  default: { color: colors.text.secondary, border: colors.border.default, bg: "rgba(255,255,255,0.04)", hoverBg: "rgba(255,255,255,0.08)" },
  brand:   { color: colors.text.primary,   border: "transparent",         bg: colors.brand.gradientBtn,  hoverBg: colors.brand.gradientBtn },
  success: { color: colors.status.success, border: colors.status.success, bg: `${colors.status.success}15`, hoverBg: `${colors.status.success}25` },
  danger:  { color: colors.status.danger,  border: colors.status.danger,  bg: `${colors.status.danger}15`,  hoverBg: `${colors.status.danger}25` },
};

export function ActionButton({ onClick, loading = false, disabled = false, tone = "default", title, children, style }) {
  const [hover, setHover] = useState(false);
  useEffect(ensureSpinKeyframes, []);

  const isDisabled = disabled || loading;
  const t = TONES[tone] || TONES.default;

  return (
    <button
      type="button"
      title={title}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: "6px 16px",
        background: isDisabled ? colors.bg.elevated : hover ? t.hoverBg : t.bg,
        border: `1px solid ${isDisabled ? colors.border.default : t.border}`,
        borderRadius: radius.md,
        color: isDisabled ? colors.text.faint : t.color,
        fontFamily: fonts.display,
        fontSize: fontSize.caption,
        fontWeight: fontWeight.bold,
        letterSpacing: "0.5px",
        cursor: isDisabled ? "default" : "pointer",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        flexShrink: 0,
        transition: "background 0.15s, border-color 0.15s, color 0.15s",
        ...style,
      }}
    >
      {loading && (
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            border: `2px solid ${t.color}40`,
            borderTopColor: t.color,
            display: "inline-block",
            animation: "actionButtonSpin 0.6s linear infinite",
            flexShrink: 0,
          }}
        />
      )}
      {loading ? "Loading…" : children}
    </button>
  );
}

export function EmptyState({ message, icon = "—" }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 0", color: colors.text.faint, fontSize: fontSize.sub, fontFamily: fonts.body }}>
      <div style={{ fontSize: fontSize.title, marginBottom: 8, opacity: 0.6 }}>{icon}</div>
      {message}
    </div>
  );
}
